import React, { FC } from 'react'
import { motion, Variants } from 'framer-motion'
import { GeometryCubeIcon } from '@/components/icons/geometry-cube-icon'
import { EinsteinsEquationIcon } from '@/components/icons/einsteins-eq'
import styles from '../styles/index.module.css'

const circleAnimation: Variants = {
  hidden: {
    y: 200,
    opacity: 0,
  },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      delay: 0.1,
      type: 'spring',
      stiffness: 200,
      damping: 10,
      mass: 1.5,
      bounce: 0.5,
    },
  },
}

const geometryAnimation: Variants = {
  hidden: { y: -50, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      delay: 0.7,
      type: 'spring',
      stiffness: 90,
    },
  },
}

const equationAnimation: Variants = {
  hidden: { scale: 0.5, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: {
      delay: 0.4,
      type: 'spring',
      bounce: 0.5,
    },
  },
}

interface Props {
  className?: string
}

export const ExamSphereAnimatedBackground: FC<Props> = ({ className = '' }) => {
  return (
    <div
      className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}
    >
      {/* Animated Background Image */}
      <motion.div
        initial={{ opacity: 0, scale: 1.8 }}
        animate={{ opacity: [0.8, 1, 0.3], scale: 1.3 }}
        transition={{ duration: 1.2, ease: 'easeOut' }}
        className="absolute inset-0 z-[1]"
        style={{
          backgroundImage: "url('/images/dashboard-cards/bg-variant-10.png')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      />

      {/* Animated Papers Image */}
      <motion.div
        initial={{
          opacity: 0,
          x: 120,
          y: 20,
          rotate: 15,
        }}
        animate={{
          opacity: [0.4, 0.7, 0.4],
          x: 0,
          y: 0,
          rotate: [8, -5, 3, -2, 0],
        }}
        transition={{
          x: { duration: 0.4, ease: 'easeOut' },
          y: { duration: 0.4, ease: 'easeOut' },
          opacity: { duration: 0.8, ease: 'easeOut' },
          rotate: {
            delay: 0.2,
            duration: 0.6,
            ease: 'easeInOut',
          },
        }}
        className="hidden lg:block absolute -bottom-10 -right-10 w-[300px] h-[300px] rounded-xl origin-left-bottom z-[2]"
        style={{
          backgroundImage: "url('/images/dashboard-cards/papers-no-marks.png')",
          backgroundSize: 'cover',
          backgroundPosition: 'bottom',
          backgroundRepeat: 'no-repeat',
        }}
      />

      {/* Animated Marks Image */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 1, 1, 0.3] }}
        transition={{
          duration: 2,
          ease: 'easeInOut',
          delay: 0.6,
        }}
        className="hidden lg:block absolute -bottom-10 -right-10 w-[300px] h-[300px] rounded-xl z-[2]"
        style={{
          backgroundImage: "url('/images/dashboard-cards/papers-marks.png')",
          backgroundSize: 'cover',
          backgroundPosition: 'bottom',
          backgroundRepeat: 'no-repeat',
        }}
      />

      {/* Animated Bouncing Circle */}
      <div className={`${styles.circleWrapperReversed} z-[1]`}>
        <motion.div
          className={styles.circleReversed}
          initial="hidden"
          animate="visible"
          variants={circleAnimation}
        />
      </div>

      {/* Animated Cube Icon */}
      <motion.div
        className={`${styles.geometryIconWrapper} z-[2]`}
        initial="hidden"
        animate="visible"
        variants={geometryAnimation}
      >
        <GeometryCubeIcon className="w-20 h-20" fill="#3157FF" />
      </motion.div>

      {/* Animated Equation Icon */}
      <motion.div
        className={`z-[2] p-1 ${styles.equationIconWrapper}`}
        initial="hidden"
        animate="visible"
        variants={equationAnimation}
      >
        <EinsteinsEquationIcon className="w-20 h-10" fill="#FF692E" />
      </motion.div>
    </div>
  )
}
